import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useNavigate, useParams } from 'react-router-dom';

import type { AppFolder, AssetFolder, LibraryImportResult } from '../../domain';
import { LIFECYCLE_STATES } from '../../domain';
import { FoldersPanel } from './FoldersPanel';
import { CollectionsPanel } from './CollectionsPanel';
import { AssetGrid } from './AssetGrid';
import { LabsPicker } from './LabsPicker';
import { SignedOutPanel } from '../../components/auth/SignedOutPanel';
import { useAuthStore } from '../../store/authStore';
import { isLatticeConfigured } from '../../services';
import { LibraryGrid } from './LibraryGrid';
import { LibrarySourceSwitch } from './LibrarySourceSwitch';
import { allAppAssetsFolder, humanizeAppType } from './assetUtils';
import type { LibrarySource } from './libraryUtils';

type GalleryTab = 'folders' | 'collections';
type Toast = { msg: string; severity: 'success' | 'error' };

/**
 * Asset gallery page. Two-pane master-detail layout: a tabbed left rail
 * (Folders / Collections) and the asset grid on the right. Folder mode adds
 * favorites + lifecycle filters; collection mode renders a collection's
 * assets. `/collections/:collectionId` deep-links straight to a collection.
 *
 * A source switch (shown when Lattice is configured) adds the shared SmartAI
 * library: the user's Lattice work, or Coral and Lattice together. Lattice
 * images and video can be imported ("Use in Coral"); the import lands in the
 * app's "All assets" folder, which the page then opens.
 */
export const AssetGalleryPage: React.FC = () => {
  const navigate = useNavigate();
  const { collectionId: routeCollectionId } = useParams<{ collectionId: string }>();

  const [tab, setTab] = useState<GalleryTab>(routeCollectionId ? 'collections' : 'folders');
  const [selectedFolder, setSelectedFolder] = useState<AssetFolder | null>(null);
  const [selectedApp, setSelectedApp] = useState<AppFolder | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(
    routeCollectionId ?? null,
  );

  const latticeAvailable = isLatticeConfigured();
  const [source, setSource] = useState<LibrarySource>('coral');
  const [toast, setToast] = useState<Toast | null>(null);

  // Folder-mode filters.
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('');

  // Keep selection in sync when the route param changes (deep link / back-fwd).
  React.useEffect(() => {
    if (routeCollectionId) {
      setTab('collections');
      setSelectedCollectionId(routeCollectionId);
    }
  }, [routeCollectionId]);

  const handleSelectFolder = (folder: AssetFolder, app: AppFolder) => {
    setSelectedFolder(folder);
    setSelectedApp(app);
  };

  const handleSelectCollection = (id: string) => {
    setSelectedCollectionId(id);
    // Reflect the selection in the URL so it's deep-linkable / shareable.
    navigate(`/collections/${id}`);
  };

  // An import is a session-less asset, so it only shows in its app's "All
  // assets" folder: switch back to Coral and open that folder.
  const handleImported = (result: LibraryImportResult) => {
    const folder = allAppAssetsFolder(result.appAgentType);
    setSource('coral');
    setTab('folders');
    setSelectedFolder(folder);
    setSelectedApp({
      appAgentType: result.appAgentType,
      name: humanizeAppType(result.appAgentType),
      folders: [],
      assetCount: 0,
    });
    setFavoritesOnly(false);
    setLifecycleFilter('');
    setToast({ msg: 'Added to your Coral assets', severity: 'success' });
  };

  const isSignedIn = useAuthStore(state => state.isAuthenticated);

  const isCollections = tab === 'collections';
  const isCoral = source === 'coral';

  // The gallery shows only the caller's own assets, so signed out there is
  // nothing to render. Say that rather than showing an empty grid, which would
  // read as "you have no work" instead of "you aren't signed in".
  if (!isSignedIn) {
    return (
      <Box sx={{ height: '100vh', bgcolor: 'background.default' }}>
        <SignedOutPanel
          what="your gallery"
          detail="Images, documents and collections you create are saved to your account."
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Elevation pass 2026-05-26 (Area 0): the gallery is now the boot
          surface, so the previous "back to /labs/image-lab" affordance is
          stale. LabsPicker takes its place above the header — chips on
          mobile, cards on desktop. */}
      <LabsPicker />

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 2,
          py: 1,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <CollectionsIcon fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600}>
          Asset Gallery
        </Typography>

        <Box sx={{ flex: 1 }} />

        <LibrarySourceSwitch
          value={source}
          onChange={setSource}
          latticeAvailable={latticeAvailable}
        />

        {/* Folder-mode filters: favorites toggle + lifecycle dropdown. */}
        {isCoral && !isCollections && (
          <>
            <Tooltip title={favoritesOnly ? 'Show all' : 'Show favorites only'}>
              <Chip
                icon={favoritesOnly ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                label="Favorites"
                size="small"
                color={favoritesOnly ? 'error' : 'default'}
                variant={favoritesOnly ? 'filled' : 'outlined'}
                onClick={() => setFavoritesOnly(v => !v)}
              />
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="lifecycle-filter-label">Lifecycle</InputLabel>
              <Select
                labelId="lifecycle-filter-label"
                label="Lifecycle"
                value={lifecycleFilter}
                onChange={e => setLifecycleFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {LIFECYCLE_STATES.map(s => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Stack>

      {!isCoral && (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <LibraryGrid
            source={source}
            onImported={handleImported}
            onImportError={msg => setToast({ msg, severity: 'error' })}
          />
        </Box>
      )}

      {isCoral && (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left rail: tab strip + the active panel. */}
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              borderRight: theme => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              bgcolor: 'background.paper',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, v: GalleryTab) => setTab(v)}
              variant="fullWidth"
              sx={{ minHeight: 40, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
            >
              <Tab value="folders" label="Folders" sx={{ minHeight: 40, py: 0 }} />
              <Tab value="collections" label="Collections" sx={{ minHeight: 40, py: 0 }} />
            </Tabs>

            {isCollections ? (
              <CollectionsPanel
                selectedId={selectedCollectionId}
                onSelect={handleSelectCollection}
              />
            ) : (
              <FoldersPanel selectedId={selectedFolder?.id ?? null} onSelect={handleSelectFolder} />
            )}
          </Box>

          {isCollections ? (
            <AssetGrid mode="collection" collectionId={selectedCollectionId} />
          ) : (
            <AssetGrid
              mode="folder"
              folder={selectedFolder}
              appName={selectedApp?.name ?? selectedApp?.appAgentType ?? null}
              favoritesOnly={favoritesOnly}
              lifecycleFilter={lifecycleFilter || null}
            />
          )}
        </Box>
      )}

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
